import { useState, useEffect } from "react";
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Platform, Alert, ActivityIndicator } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import * as ImagePicker from "expo-image-picker";
import { getCleanNameFromId } from "../../utils/helpers/helpers";
import { useSelector } from "react-redux";
import { firebaseApp } from "../../firebase/config";
import BackgroundWithImage from "../../components/ScreenImageBackground";
import IsFaultyCard from "../../components/IsFaultyCard";
import UploadPhotoBox from '../../components/UploadPhotoBox'
import { doc, collection, getCountFromServer, getFirestore, updateDoc, query, where, setDoc } from "firebase/firestore";
import { uploadImage } from "../../utils/helpers/functions";
import {
    executeFirestoreWriteWithQueue,
    isDeviceOnline,
} from "../../utils/offlineWriteQueue";
import { queueDeferredMediaUpload } from "../../utils/deferredMediaQueue";

const SubmitScreen = ({ navigation }) => {
    const [isFaulty, setIsFaulty] = useState(false)
    const [comment, setComment] = useState('')
    const [loading, setLoading] = useState(false)
    const [media, setMedia] = useState([])
    let imageDownloadUrls = [];
    const currentClean = useSelector((state) => state.buildingList.currentClean);
    const currentDrop = useSelector((state) => state.buildingList.currentDrop);
    const buildingName = useSelector((state) => state.buildingList.buildingName);
    const currentCleanData = useSelector(
        (state) => state.buildingList.currentCleanData
    );
    const userUid = useSelector((state) => state.auth.uid);
    const building = useSelector((state) => state.buildingList.buildingData);
    const date = new Date();

    const db = getFirestore(firebaseApp);
    const buildingRef = doc(db, "BuildingsX", buildingName?.site_id);

    const dropRef = doc(buildingRef, "currentYear", currentClean, "drops", currentDrop.name);

    const cleanRef = doc(buildingRef, "currentYear", currentClean);
    const defectsRef = collection(buildingRef, "defects");

    useEffect(() => {
        (async () => {
            if (Platform.OS !== "web") {
                const { status } =
                    await ImagePicker.requestMediaLibraryPermissionsAsync();
                if (status !== "granted") {
                    alert("Sorry, we need camera roll permissions to make this work!");
                }
            }
        })();
    }, []);

    const selectItem = () => {
        setIsFaulty(!isFaulty)
    }

    const pickImage = async () => {
        try {
            if (media.length >= 5) {
                Alert.alert('Media limit', 'You can only upload up to 5 images.');
                return;
            }
            let result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsMultipleSelection: true,
                quality: 1,
                selectionLimit: 5 - media.length,
            });

            const assets = result?.assets || (result?.uri ? [{ uri: result.uri }] : []);
            const uris = assets.map(a => a.uri).filter(Boolean);
            if (uris.length) {
                setMedia(prev => [...prev, ...uris].slice(0, 5));
            }
        } catch (err) {
            console.log('Image pick error', err);
        }
    };

    const submitHandler = async () => {
        try {
            setLoading(true)

            const isOnline = await isDeviceOnline();
            const shouldQueueMediaUpload = !isOnline && media.length > 0;

            if (isFaulty && media.length === 0) {
                throw new Error("You must upload an image if reporting a fault.")
            }

            if (media.length > 0) {
                imageDownloadUrls = shouldQueueMediaUpload ? [] : await uploadImage(media);
            }

            const defectDocRef = isFaulty ? doc(defectsRef) : null;
            const deferredTargets = [];

            if (shouldQueueMediaUpload) {
                deferredTargets.push({ path: dropRef.path, patchType: "first-image-url" });
                if (defectDocRef) {
                    deferredTargets.push({ path: defectDocRef.path, patchType: "images-array" });
                }
            }

            const queuedMediaUploadId = shouldQueueMediaUpload
                ? await queueDeferredMediaUpload({
                    label: "drop-submit",
                    mediaUris: media,
                    storageBasePath: `drop-submissions/${buildingName?.site_id}/${currentDrop.name}`,
                    documentTargets: deferredTargets,
                })
                : null;

            const { queued } = await executeFirestoreWriteWithQueue({
                db,
                queueLabel: "drop-submit",
                writeOperation: async () => {
                    await updateDoc(dropRef, {
                        cleanStatus: !isFaulty ? "Cleaned" : "Faulty",
                        isComplete: true,
                        date: date,
                        comments: comment,
                        isFaulty: isFaulty,
                        image_url: imageDownloadUrls.length > 0 ? imageDownloadUrls[0] : '',
                        mediaUploadStatus: shouldQueueMediaUpload ? "queued" : "uploaded",
                        pendingMediaCount: shouldQueueMediaUpload ? media.length : 0,
                        queuedMediaUploadId: queuedMediaUploadId || null,
                    })

                    await updateDoc(buildingRef, {
                        currentClean: currentClean,
                        currentCleanRef: cleanRef,
                        lastCleaned: date,
                        lastDropCleaned: currentDrop.name
                    })

                    if (isFaulty) {
                        const documentToAdd = {
                            defectReportDate: date,
                            fixDate: null,
                            defectType: 'building',
                            defectTypeLabel: 'building',
                            inService: "",
                            summary: comment.trim(),
                            priority: 'medium',
                            status: 'open',
                            isResolved: false,
                            completed: false,
                            source: 'SubmitScreen',
                            images: shouldQueueMediaUpload ? [] : imageDownloadUrls,
                            mediaUploadStatus: shouldQueueMediaUpload ? "queued" : "uploaded",
                            pendingMediaCount: shouldQueueMediaUpload ? media.length : 0,
                            queuedMediaUploadId: queuedMediaUploadId || null,
                            completedBy: userUid,
                            equipmentId: currentDrop.equipmentId || '',
                            dropId: currentDrop.name,
                            cleanId: currentClean,
                        }
                        await setDoc(defectDocRef, documentToAdd)
                    }
                },
            })

            if (currentCleanData) {
                const dropsCol = collection(cleanRef, "drops");
                const q = query(dropsCol, where("isComplete", "==", true));
                const snapshot = await getCountFromServer(q);
                const noOfDropsCleaned = snapshot.data().count;

                if (noOfDropsCleaned === 1) {
                    await updateDoc(cleanRef, { startDate: date })
                } else if (noOfDropsCleaned === building.noDrops - 1) {
                    await updateDoc(cleanRef, { finishDate: date })
                }
            }

            submitted(queued)

        } catch (error) {
            alert(error.message)
            console.log(error)
        }
    }

    const submitted = (queued = false) => {
        const alertMessage = queued
            ? media.length > 0
                ? `${currentDrop.name} saved offline. Photos queued for upload. Remember to fill your "After Clean form" once synced.`
                : `${currentDrop.name} saved offline and queued for sync. Remember to fill your "After Clean form" once synced.`
            : `${currentDrop.name} just completed. Remember to fill your "After Clean form". Thank you.`

        if (Platform.OS === 'web') {
            alert(alertMessage)
            setLoading(false)
            navigation.navigate('Thanks')
        } else {
            Alert.alert('Done', alertMessage, [
                {
                    text: 'OK',
                    onPress: () => {
                        setLoading(false)
                        navigation.navigate('Thanks')

                    }
                }
            ])
        }
    }

    if (loading) return <ActivityIndicator style={{ flex: 1 }} size='large' color='#5e99fa' />

    return (
        <KeyboardAwareScrollView
            contentContainerStyle={{ flex: 1 }}
            enableOnAndroid={true}
            automaticallyAdjustContentInsets={true}
            enableAutomaticScroll={(Platform.OS === 'ios')}
        >

            <BackgroundWithImage>
                <View style={styles.contentContainer}>
                    <Text style={styles.almostThereText}>Almost there...</Text>

                    <Text style={styles.infoText}>Clean: {getCleanNameFromId(currentClean)}</Text>
                    <Text style={styles.infoText}>Drop: {currentDrop.name}</Text>

                    <View style={styles.reportingBox}>
                        <IsFaultyCard isFaulty={isFaulty} selectItem={selectItem} />

                        <TextInput
                            value={comment}
                            placeholder="Enter a comment..."
                            numberOfLines={12}
                            multiline
                            textAlignVertical="top"
                            returnKeyType="done"
                            onChangeText={(text) => setComment(text)}
                            style={styles.commentsInput}
                        />

                        <UploadPhotoBox pickImage={pickImage} media={media} />
                    </View>

                    <TouchableOpacity style={styles.submitButtonBox} onPress={submitHandler}>
                        <Text style={styles.submitButtonText}>Submit</Text>
                    </TouchableOpacity>
                </View>
            </BackgroundWithImage>

        </KeyboardAwareScrollView>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1
    },
    contentContainer: {
        marginHorizontal: 30,
        marginVertical: 50
    },
    almostThereText: {
        color: '#fff',
        fontSize: 40,
        fontWeight: '700',
        marginVertical: 20
    },
    infoText: {
        color: '#fff',
        fontSize: 20
    },
    reportingBox: {
        marginVertical: 50,
        height: 350
    },
    commentsInput: {
        backgroundColor: '#fff',
        marginVertical: 10,
        padding: 10,
        borderRadius: 10,
        height: 240
    },
    submitButtonBox: {
        alignItems: 'center'
    },
    submitButtonText: {
        color: '#fff',
        fontSize: 30,
        fontWeight: '700'
    },
    imagePickerContainer: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'white',
        zIndex: 9,
    }
})

export default SubmitScreen